/**
 * CostTrendChart Component
 *
 * Displays a line chart showing cost trends over time.
 * Shows total costs, fixed costs, and variable costs as separate lines.
 *
 * Part of US-P7-001 Step 5: Cost Summary Dashboard
 */

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'

/**
 * Format currency for axis
 *
 * @param {number} value - Value to format
 * @returns {string} Formatted currency string
 */
function formatCurrency(value) {
  if (value >= 1000) {
    return `£${(value / 1000).toFixed(1)}k`
  }
  return `£${value.toFixed(0)}`
}

/**
 * Format month label for x-axis
 *
 * @param {string} month - Month string in YYYY-MM-DD format
 * @returns {string} Formatted month label
 */
function formatMonth(month) {
  const date = new Date(month)
  return date.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })
}

/**
 * Custom tooltip component
 */
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null

  const date = new Date(label)
  const monthYear = date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })

  return (
    <div className="bg-white border border-neutral-200 rounded-lg shadow-lg p-3">
      <p className="font-medium text-neutral-900 mb-2">{monthYear}</p>
      {payload.map((entry, index) => (
        <div key={index} className="flex items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-neutral-600">{entry.name}</span>
          </div>
          <span className="font-medium text-neutral-900">
            {new Intl.NumberFormat('en-GB', {
              style: 'currency',
              currency: 'GBP',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }).format(entry.value)}
          </span>
        </div>
      ))}
    </div>
  )
}

/**
 * CostTrendChart Component
 *
 * @param {Object} props
 * @param {Array} props.trendData - Array of trend data points
 * @param {boolean} [props.isLoading] - Loading state
 * @param {string} [props.className] - Additional CSS classes
 */
export default function CostTrendChart({
  trendData,
  isLoading = false,
  className = ''
}) {
  // Transform trend data for chart
  const chartData = trendData
    ? trendData.map((point) => ({
        month: point.month || point.period_start,
        total: point.total_costs || 0,
        fixed: point.total_fixed_costs || point.fixed_costs || 0,
        variable: point.total_variable_costs || point.variable_costs || 0
      }))
    : []

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>Cost Trend</CardTitle>
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <Skeleton className="h-80 w-full" />
        ) : chartData.length === 0 ? (
          <div className="flex items-center justify-center h-80 text-neutral-500">
            <p>No trend data available</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <LineChart
              data={chartData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#6b7280' }}
                tickFormatter={formatMonth}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#6b7280' }}
                tickFormatter={formatCurrency}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
                iconType="line"
              />

              {/* Total costs line */}
              <Line
                type="monotone"
                dataKey="total"
                name="Total"
                stroke="#111827"
                strokeWidth={2}
                dot={{ fill: '#111827', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#111827', strokeWidth: 2 }}
              />

              {/* Fixed costs line */}
              <Line
                type="monotone"
                dataKey="fixed"
                name="Fixed"
                stroke="#6366f1"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ fill: '#6366f1', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#6366f1', strokeWidth: 2 }}
              />

              {/* Variable costs line */}
              <Line
                type="monotone"
                dataKey="variable"
                name="Variable"
                stroke="#10b981"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#10b981', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * CostTrendChartSkeleton - Loading state for CostTrendChart
 *
 * @param {Object} props
 * @param {string} [props.className] - Additional CSS classes
 */
export function CostTrendChartSkeleton({ className = '' }) {
  return (
    <Card className={className}>
      <CardHeader>
        <Skeleton className="h-6 w-32" />
      </CardHeader>

      <CardContent>
        <Skeleton className="h-80 w-full" />
      </CardContent>
    </Card>
  )
}
